import { useMemo } from 'react';
import { Tag, Tooltip, Typography } from 'antd';
import {
  SmartDataTableProps,
  SmartTableColumn,
} from '@src/components/SmartDataTable/types';
import { useStatusRender } from './useStatusRender';
import { useServiceTypeRender } from './useServiceTypeRender';
import {
  useScheduledDateRender,
  useUserAvatarRender,
  useUserTimestampRender,
} from '@src/hooks';
import {
  STATUS_FILTER_OPTIONS,
  SERVICE_TYPE_FILTER_OPTIONS,
} from '../constants';
import { FaWhatsapp } from 'react-icons/fa';
import dayjs from 'dayjs';

const { Text } = Typography;

interface UseAppointmentsColumnsOptions {
  showCreatedAt?: boolean;
}

export const useAppointmentsColumns = (
  options: UseAppointmentsColumnsOptions = {}
): SmartTableColumn[] => {
  const { showCreatedAt = true } = options;
  const getStatusConfig = useStatusRender();
  const getServiceTypeConfig = useServiceTypeRender();
  const renderScheduledDate = useScheduledDateRender();
  const renderUserAvatar = useUserAvatarRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });

  // Handle patient info click to redirect to patient details
  const handlePatientInfoClick = (record: any, e: any) => {
    e.stopPropagation();
    // Only redirect if patient_id exists and is not 0
    if (record.patient_id && record.patient_id !== 0) {
      const url = `${window.location.origin}/patients/${record.patient_id}`;
      window.open(url, '_blank');
    }
  };

  return useMemo(() => {
    const baseColumns: SmartTableColumn[] = [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        sorter: true,
        minWidth: 80,
        ellipsis: true,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'scheduled_datetime',
        title: 'Scheduled Date & Time',
        dataIndex: 'scheduled_datetime',
        sorter: true,
        minWidth: 180,
        filterable: true,
        filterType: 'daterange',
        render: (scheduledDate: any, record: any) =>
          renderScheduledDate(scheduledDate, record.status),
      },
      {
        key: 'follow_up_date',
        title: 'Follow Up Date',
        dataIndex: 'follow_up_date',
        sorter: true,
        minWidth: 180,
        filterable: true,
        filterType: 'daterange',
        render: (followUpDate: any, record: any) =>
          renderScheduledDate(followUpDate, record.status),
      },
      {
        key: 'patient',
        title: 'Patient Info',
        dataIndex: 'patient_name',
        sorter: false,
        minWidth: 200,
        render: (patientName: any, record: any) => {
          const isClickable = record.patient_id && record.patient_id !== 0;

          return (
            <div
              onClick={e => handlePatientInfoClick(record, e)}
              style={{
                cursor: isClickable ? 'pointer' : 'default',
                padding: '2px',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                border: '1px solid transparent',
              }}
              onMouseEnter={e => {
                if (isClickable) {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                  e.currentTarget.style.borderColor = '#d9d9d9';
                }
              }}
              onMouseLeave={e => {
                if (isClickable) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'transparent';
                }
              }}
              title={isClickable ? 'Click to view patient details' : undefined}
            >
              <div style={{ fontWeight: 500, color: '#262626' }}>
                {patientName || 'N/A'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: '13px',
                    color: '#8c8c8c',
                  }}
                >
                  +{record.patient_mobile_no || 'No mobile'}
                </div>
                {record.patient_mobile_no && (
                  <Tooltip title="Open WhatsApp">
                    <FaWhatsapp
                      style={{ fontSize: '18px', color: '#25D366' }}
                      onClick={e => {
                        e.stopPropagation();
                        if (record.patient_mobile_no) {
                          window.open(
                            `https://web.whatsapp.com/send/?phone=${record.patient_mobile_no}`,
                            '_blank'
                          );
                        }
                      }}
                    />
                  </Tooltip>
                )}
              </div>

              {record.patient_status && (
                <Tag
                  color={record.patient_status === 'NEW' ? 'green' : 'blue'}
                  style={{ marginTop: '2px', fontSize: '10px' }}
                >
                  {record.patient_status}
                </Tag>
              )}
            </div>
          );
        },
      },
      {
        key: 'appointed_to',
        title: 'Appointed To',
        dataIndex: 'appointed_to',
        sorter: false,
        minWidth: 180,
        render: (appointed_to: any) => renderUserAvatar(appointed_to),
      },
      {
        key: 'service_type',
        title: 'Service Type',
        dataIndex: 'service_type',
        sorter: true,
        minWidth: 140,
        filterable: true,
        filterType: 'multiselect',
        filterOptions: [...SERVICE_TYPE_FILTER_OPTIONS],
        render: (serviceType: string) => getServiceTypeConfig(serviceType),
      },
      {
        key: 'duration_in_mins',
        title: 'Duration',
        dataIndex: 'duration_in_mins',
        sorter: true,
        minWidth: 100,
        render: (mins: number) => <Text>{mins ? `${mins} mins` : '-'}</Text>,
      },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'status',
        sorter: true,
        minWidth: 120,
        filterable: true,
        filterType: 'multiselect',
        filterOptions: [...STATUS_FILTER_OPTIONS],
        render: (status: string) => {
          const config = getStatusConfig(status);
          return (
            <Tag
              icon={config.icon}
              style={{
                fontWeight: 500,
                backgroundColor: config.bgColor,
                color: config.textColor,
                borderColor: config.borderColor,
                border: `1px solid ${config.borderColor}`,
              }}
            >
              {config.text}
            </Tag>
          );
        },
      },
      {
        key: 'user_timestamp',
        title: 'User & Timestamp',
        dataIndex: 'user_info',
        sorter: false,
        minWidth: 200,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ];

    // Conditionally add the created_at column with date range filter
    if (showCreatedAt) {
      baseColumns.push({
        key: 'created_at',
        title: 'Created Date',
        dataIndex: 'created_at',
        sorter: true,
        minWidth: 140,
        filterable: true,
        filterType: 'daterange' as const,
        render: () => null, // Hide the column content but keep filter functionality
        hidden: true, // Hide the column from display
      });
    }

    return baseColumns;
  }, [
    getStatusConfig,
    getServiceTypeConfig,
    renderScheduledDate,
    renderUserAvatar,
    userTimestampRender,
    showCreatedAt,
  ]);
};
